import { AnyAction } from '@reduxjs/toolkit';
import { toggleDeleteModalState } from 'store/slices/DeleteModalSlice';
import { toggleRecalculateLayoutModalState } from 'store/slices/EditFacetModalSlice';
import { toggleOrientationModalState } from 'store/slices/ChangeOrientationModalSlice';
import { toggleManualToolModalState } from 'store/slices/ManualToolModal';
import {
	ChangeOrientationIcon,
	AdjustShadingIcon,
	RecalculateLayoutIcon,
	EditPitchOutlineIcon,
	EditPitchIcon,
	DeleteIcon,
} from 'assets/svg';

export interface Option {
	label: string;
	action: () => AnyAction;
	actionId: string;
	icon: JSX.Element;
	show: boolean;
}

const iconStyle = { marginRight: '5px', minWidth: '16px' };

export interface SolarOptionsContext {
	drawModeEnabled: boolean;
	hadTransition: boolean;
	showChangeOrientationOption: boolean;
	showRemoveShadingOption: boolean;
	showEditPitchAndShadingOption: boolean;
	showEditPitchOption: boolean;
	isFluxPatched: boolean;
	shadingActionType: 'adjust' | 'undo' | 'init';
	/** Fires the remove/adjust-shading mutation; returns the action dispatched afterwards. */
	triggerRemoveShading: () => AnyAction;
}

export function getSolarOptions(ctx: SolarOptionsContext): Option[] {
	const {
		drawModeEnabled,
		hadTransition,
		showChangeOrientationOption,
		showRemoveShadingOption,
		showEditPitchAndShadingOption,
		showEditPitchOption,
		isFluxPatched,
		shadingActionType,
		triggerRemoveShading,
	} = ctx;

	return [
		{
			label: 'Change Orientation',
			actionId: 'rotate-roof-segments',
			show: !drawModeEnabled && showChangeOrientationOption,
			action: () => toggleOrientationModalState({ open: true, isLoading: false }),
			icon: <ChangeOrientationIcon style={iconStyle} width={16} height={16} />,
		},
		{
			label: shadingActionType === 'undo' ? 'Undo Remove Shading' : 'Adjust Shading',
			actionId: !isFluxPatched ? 'remove-roof-segment-shading' : 'undo-remove-roof-segment-shading',
			show: !drawModeEnabled && showRemoveShadingOption && !hadTransition,
			action: triggerRemoveShading,
			icon: <AdjustShadingIcon style={iconStyle} width={16} height={18.6} />,
		},
		{
			label: 'Recalculate Layout',
			actionId: 'reset-layout',
			show: !drawModeEnabled,
			action: () => toggleRecalculateLayoutModalState({ value: true }),
			icon: <RecalculateLayoutIcon style={iconStyle} width={18} height={18} />,
		},
		{
			label: 'Edit Pitch and Shading',
			actionId: 'translate-panels-to-new-position',
			show: !drawModeEnabled && showEditPitchAndShadingOption,
			action: () => toggleManualToolModalState({ value: true }),
			icon: <EditPitchIcon style={iconStyle} width={16} height={16} />,
		},
		{
			label: 'Edit Pitch',
			actionId: 'translate-panels-to-new-position',
			show: !drawModeEnabled && showEditPitchOption,
			action: () => toggleManualToolModalState({ value: true }),
			icon: <EditPitchOutlineIcon style={iconStyle} width={16} />,
		},
		{
			label: 'Delete Facet',
			actionId: 'edit-roof-segments',
			show: true,
			action: () => toggleDeleteModalState({ value: true }),
			icon: <DeleteIcon style={{ marginRight: '5px' }} width={16} height={16} />,
		},
	];
}

export function getRoofOptions(ctx: { drawModeEnabled: boolean }): Option[] {
	return [
		{
			label: 'Edit Pitch',
			actionId: 'update-roof-pitch',
			show: !ctx.drawModeEnabled,
			action: () => toggleManualToolModalState({ value: true }),
			icon: <EditPitchIcon style={{ marginRight: '5px' }} width={16} height={16} />,
		},
		{
			label: 'Delete Facet',
			actionId: 'edit-roof-segments',
			show: true,
			action: () => toggleDeleteModalState({ value: true }),
			icon: <DeleteIcon style={{ marginRight: '5px' }} width={16} height={16} />,
		},
	];
}
